import d from "./DOM.js";
import { withCsrf } from "./csrf.js";

export default abstract class tradeJournal {
	private static modal: HTMLElement;
	private static form: HTMLFormElement;
	private static currentTicket: string | null = null;
	private static saveTimeout: any;
	private static strategies: any[] = [];
	private static quickMode = false;
	private static groupTickets: string[] = [];
	
	public static init() {
		tradeJournal.modal = d.qs('#journalModal');
		tradeJournal.form = d.qs<HTMLFormElement>('#journalForm');
		
		if (!tradeJournal.modal) { return; }
		
		// Open Modal
		d.e('click', tradeJournal.openModal, d.qsa('.journalBtn'));
		
		// Close Modal
		d.e('click', tradeJournal.closeModal, d.qsa('.close-modal'));
		d.e('click', (e: MouseEvent) => { if (e.target === tradeJournal.modal) { tradeJournal.closeModal(); } }, tradeJournal.modal);
		
		// Tabs
		d.e('click', tradeJournal.switchTab, d.qsa('.tab-btn'));
		
		// Autosave
		d.e('input', tradeJournal.triggerAutosave, tradeJournal.form);
		d.e('change', tradeJournal.triggerAutosave, tradeJournal.form);
		
		// Manual Save
		d.e('click', async (e) => {
			e.preventDefault();
			const success = await tradeJournal.save();
			if (success) {
				if (tradeJournal.quickMode) { tradeJournal.finishQuick(); }
				else { tradeJournal.closeModal(); }
			}
		}, d.qs('#saveJournalBtn'));
		
		// Sliders
		d.e('input', (e: Event) => {
			const target = e.target as HTMLInputElement;
			const display = target.nextElementSibling as HTMLElement;
			if (display && display.classList.contains('slider-value')) { display.textContent = target.value; }
		}, d.qsa('.slider'));
		
		// Upload Screenshot. Bound to BOTH the button and the file picker: requiring a separate
		// click after choosing a file is how an upload silently never happened (2026-08-02) —
		// picking the image is the point at which the user believes they have uploaded it.
		d.e('click', tradeJournal.uploadScreenshot, d.qs('#uploadScreenshotBtn'));
		d.e('change', tradeJournal.uploadScreenshot, d.qs('#screenshotInput'));
		
		tradeJournal.loadStrategies();
	}
	
	/** Write a visible line under the upload controls. Silence is how the old flow lost files. */
	private static uploadStatus(message: string) {
		const el = d.qs<HTMLElement>('#uploadStatus');
		if (el) { el.textContent = message; }
	}

	private static async uploadScreenshot() {
		const input = d.qs<HTMLInputElement>('#screenshotInput');
		if (!input) { return; }

		// Previously this returned silently when nothing was picked, so a user who pressed Upload
		// without a selected file saw no request, no error and no file — which is exactly how a
		// screenshot went missing with "no errors" (2026-08-02). Say something instead.
		if (!input.files || input.files.length === 0) {
			tradeJournal.uploadStatus('Choose an image first.');
			return;
		}

		if (!tradeJournal.currentTicket) {
			tradeJournal.uploadStatus('Open a trade before uploading.');
			return;
		}

		const file = input.files[0];
		const formData = new FormData();
		formData.append('screenshot', file);
		formData.append('dealTicket', tradeJournal.currentTicket!);
		
		const btn = d.qs<HTMLButtonElement>('#uploadScreenshotBtn');
		const originalText = btn ? btn.textContent : 'Upload';
		if (btn) {
			btn.textContent = 'Uploading...';
			btn.disabled = true;
		}
		tradeJournal.uploadStatus(`Uploading ${file.name}…`);

		try {
			const res = await fetch('/api/trade-journal/upload', {
				method: 'POST',
				// No Content-Type: fetch sets the multipart boundary for FormData itself.
				headers: withCsrf(),
				body: formData
			});

			if (res.ok) {
				const data = await res.json();
				tradeJournal.addScreenshotToGrid(data);
				input.value = ''; // Clear input
				tradeJournal.uploadStatus('Screenshot saved.');
			} else {
				// Report what actually went wrong. A bare "Upload failed" cost a real debugging
				// session: the status code is the whole diagnosis (403 = session, 413 = too big).
				const detail = await res.json().catch(() => null);
				const reason = detail?.error
					?? (res.status === 413 ? 'that image is too large' : `server said ${res.status}`);
				tradeJournal.uploadStatus(`Upload failed — ${reason}`);
				alert(`Upload failed — ${reason}`);
			}
		} catch (err) {
			console.error(err);
			tradeJournal.uploadStatus('Upload failed — check your connection.');
			alert('Upload failed — check your connection.');
		} finally {
			if (btn) {
				btn.textContent = originalText;
				btn.disabled = false;
			}
		}
	}
	
	private static addScreenshotToGrid(data: { url: string, uuid: string }) {
		const grid = d.qs<HTMLElement>('#screenshotsList');
		if (!grid) { return; }
		
		const div = document.createElement('div');
		div.className = 'screenshot-item';
		div.style.position = 'relative';
		
		const img = document.createElement('img');
		img.src = data.url;
		img.style.width = '100%';
		img.style.borderRadius = '4px';
		img.style.cursor = 'pointer';
		img.onclick = () => { window.open(data.url, '_blank'); };
		
		const delBtn = document.createElement('button');
		delBtn.innerHTML = '&times;';
		delBtn.style.position = 'absolute';
		delBtn.style.top = '5px';
		delBtn.style.right = '5px';
		delBtn.style.background = 'rgba(220, 38, 38, 0.9)';
		delBtn.style.color = 'white';
		delBtn.style.border = 'none';
		delBtn.style.borderRadius = '50%';
		delBtn.style.width = '24px';
		delBtn.style.height = '24px';
		delBtn.style.cursor = 'pointer';
		delBtn.style.display = 'flex';
		delBtn.style.alignItems = 'center';
		delBtn.style.justifyContent = 'center';
		delBtn.style.fontSize = '18px';
		delBtn.style.lineHeight = '1';
		delBtn.title = 'Delete Screenshot';
		
		delBtn.onclick = async (e) => {
			e.stopPropagation();
			if (confirm('Are you sure you want to delete this screenshot?')) { await tradeJournal.deleteScreenshot(data.uuid, div); }
		};
		
		div.appendChild(img);
		div.appendChild(delBtn);
		grid.appendChild(div);
	}
	
	private static async deleteScreenshot(uuid: string, element: HTMLElement) {
		try {
			const res = await fetch(`/api/trade-journal/screenshot/${uuid}`, {
				method: 'DELETE',
				headers: withCsrf()
			});
			
			if (res.ok) { element.remove(); }
			else { alert('Failed to delete screenshot'); }
		} catch (err) {
			console.error(err);
			alert('Error deleting screenshot');
		}
	}
	
	public static async loadStrategies() {
		try {
			const res = await fetch('/api/strategies');
			if (res.ok) { tradeJournal.strategies = await res.json(); }
		} catch (err) {
			console.error('Failed to load strategies', err);
		}
	}
	
	private static populateStrategyDropdown() {
		const select = d.qs<HTMLSelectElement>('#journalStrategy');
		if (!select) { return; }
		
		const currentVal = select.value;
		select.innerHTML = '<option value="">Select Strategy...</option>';
		
		// Group by type
		const system = tradeJournal.strategies.filter(s => s.type === 'SYSTEM');
		const user = tradeJournal.strategies.filter(s => s.type === 'USER');
		
		if (user.length) {
			const group = document.createElement('optgroup');
			group.label = 'My Strategies';
			
			user.forEach(s => {
				const opt = document.createElement('option');
				opt.value = s.id;
				opt.textContent = s.label;
				group.appendChild(opt);
			});
			
			select.appendChild(group);
		}

		if (system.length) {
			const group = document.createElement('optgroup');
			group.label = 'System Strategies';
			
			system.forEach(s => {
				const opt = document.createElement('option');
				opt.value = s.id;
				opt.textContent = s.label;
				group.appendChild(opt);
			});
			
			select.appendChild(group);
		}
		
		if (currentVal) { select.value = currentVal; }
	}
	
	private static async openModal(e: MouseEvent) {
		const btn = (e.target as HTMLElement).closest('.journalBtn') as HTMLElement;
		await tradeJournal.openForTicket(btn.dataset.ticket || '', btn.dataset.account || '');
	}

	// Open and pre-fill the journal for a specific ticket without a source button (used by the MT5 quick-journal deep link).
	public static async openForTicket(ticket: string, account: string) {
		
		if (!ticket || !account) { return; }
		
		tradeJournal.currentTicket = ticket;
		
		d.qs<HTMLElement>('#journalTicketDisplay').textContent = `#${ticket}`;
		d.qs<HTMLInputElement>('#dealTicket').value = ticket;
		d.qs<HTMLInputElement>('#accountNumber').value = account;
		
		// Reset form
		tradeJournal.form.reset();
		d.qsa<HTMLElement>('.slider-value').forEach(el => el.textContent = '5');
		
		// Reset screenshots and progress
		const grid = d.qs<HTMLElement>('#screenshotsList');
		if (grid) { grid.innerHTML = ''; }
		tradeJournal.updateProgress(0);
		
		// Reset tabs to first one
		d.qsa<HTMLElement>('.tab-btn').forEach(b => b.classList.remove('active'));
		d.qsa<HTMLElement>('.tab-content').forEach(c => c.classList.remove('active'));
		
		tradeJournal.populateStrategyDropdown();
		
		const firstTabBtn = d.qs<HTMLElement>('.tab-btn[data-tab="setup"]');
		const firstTabContent = d.qs<HTMLElement>('#setup');
		
		if (firstTabBtn) { firstTabBtn.classList.add('active'); }
		if (firstTabContent) { firstTabContent.classList.add('active'); }
		
		// Load data
		try {
			const res = await fetch(`/api/trade-journal/${ticket}`);
			const data = await res.json();
			if (data && data.id) {
				tradeJournal.populateForm(data);
			} else if (data && Array.isArray(data.screenshots)) {
				// No journal row yet, but screenshots can exist on their own (they are keyed on the
				// ticket). Render them, or an image uploaded before any field was filled in looks
				// like it vanished.
				data.screenshots.forEach((s: { url: string; uuid: string }) => tradeJournal.addScreenshotToGrid(s));
			}
		} catch (err) {
			console.error('Failed to load journal', err);
		}
		
		d.toggleClass(tradeJournal.modal, 'hidden', false);
	}
	
	private static closeModal() {
		d.toggleClass(tradeJournal.modal, 'hidden', true);
		tradeJournal.currentTicket = null;

		if (tradeJournal.quickMode) {
			const status = d.qs<HTMLElement>('#quickJournalStatus');
			if (status) { status.textContent = 'You can close this tab.'; }
		}
	}
	
	private static switchTab(e: MouseEvent) {
		const btn = e.target as HTMLElement;
		const tabId = btn.dataset.tab;
		
		d.qsa<HTMLElement>('.tab-btn').forEach(b => b.classList.remove('active'));
		btn.classList.add('active');
		
		d.qsa<HTMLElement>('.tab-content').forEach(c => c.classList.remove('active'));
		d.qs<HTMLElement>(`#${tabId}`).classList.add('active');
	}
	
	private static populateForm(data: any) {
		// Clear screenshots
		const grid = d.qs<HTMLElement>('#screenshotsList');
		if (grid) { grid.innerHTML = ''; }
		if (data.screenshots && Array.isArray(data.screenshots)) { data.screenshots.forEach((s: any) => tradeJournal.addScreenshotToGrid(s)); }
		
		for (const key in data) {
			const field = tradeJournal.form.elements.namedItem(key) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
			
			if (field) {
				if (field.type === 'checkbox') {
					(field as HTMLInputElement).checked = data[key];
				} else {
					let val = data[key];
					// Handle boolean selects coming as 1/0 from DB
					if (key === 'followedPlan' || key === 'wouldTakeAgain') {
						if (val === 1) { val = 'true'; }
						else if (val === 0) { val = 'false'; }
					}
					
					field.value = val;
					// Update slider display if needed
					if (field.classList.contains('slider')) {
						const display = field.nextElementSibling as HTMLElement;
						if (display) { display.textContent = data[key]; }
					}
				}
			}
		}
		
		tradeJournal.updateProgress(data.completionScore || 0);
	}
	
	private static triggerAutosave(e?: Event) {
		// The file picker lives inside #journalForm, so choosing an image bubbles a `change` up to
		// the form's autosave. That save carries no new field data and races the upload; skip it.
		if ((e?.target as HTMLElement | undefined)?.id === 'screenshotInput') { return; }

		d.qs<HTMLElement>('#saveStatus').textContent = 'Saving...';
		clearTimeout(tradeJournal.saveTimeout);
		tradeJournal.saveTimeout = setTimeout(tradeJournal.save, 1000);
	}
	
	private static async save(): Promise<boolean> {
		if (!tradeJournal.currentTicket) { return false; }
		
		const formData = new FormData(tradeJournal.form);
		const data: any = {};
		formData.forEach((value, key) => { data[key] = value; });
		
		// Handle booleans
		const handleBool = (key: string) => {
			if (data[key] === 'true') { data[key] = true; }
			else if (data[key] === 'false') { data[key] = false; }
			else { data[key] = ''; } // Send empty string for null
		};
		
		handleBool('followedPlan');
		handleBool('wouldTakeAgain');
		
		try {
			// In quick mode one journalling action applies to every ticket in the setup
			// (e.g. both fib entries): same values, a separate entry per ticket/screenshot.
			const targets = (tradeJournal.quickMode && tradeJournal.groupTickets.length > 0) ? tradeJournal.groupTickets : [tradeJournal.currentTicket];
			let result: any = null;

			for (const ticket of targets) {
				const res = await fetch('/api/trade-journal/save', {
					method: 'POST',
					headers: withCsrf({ 'Content-Type': 'application/json' }),
					body: JSON.stringify({ ...data, dealTicket: ticket })
				});

				// Never parse an error body as JSON: the host's CSRF rejection used to be plain
				// text, so `res.json()` threw a SyntaxError that masked the real 403 in the log.
				if (!res.ok) {
					d.qs<HTMLElement>('#saveStatus').textContent = res.status === 403
						? 'Not saved — sign in again'
						: 'Error saving';
					return false;
				}

				result = await res.json();
				if (!result.success) { return false; }
			}
			
			if (result.success) {
				d.qs<HTMLElement>('#saveStatus').textContent = 'Saved';
				tradeJournal.updateProgress(result.completionScore);
				
				// Update button on main table
				const btn = d.qs<HTMLElement>(`.journalBtn[data-ticket="${tradeJournal.currentTicket}"]`);
				
				if (btn) {
					btn.textContent = `${result.completionScore}%`;
					btn.className = 'journalBtn'; // reset
					
					if (result.completionScore >= 80) { btn.classList.add('btn-success'); }
					else if (result.completionScore >= 40) { btn.classList.add('btn-warning'); }
					else { btn.classList.add('btn-danger'); }
				}
				
				return true;
			}
			
			return false;
		} catch (err) {
			console.error('Save failed', err);
			d.qs<HTMLElement>('#saveStatus').textContent = 'Error saving';
			return false;
		}
	}
	
	private static updateProgress(score: number) {
		d.qs<HTMLElement>('#journalProgressBar').style.width = `${score}%`;
		d.qs<HTMLElement>('#journalScore').textContent = `${score}%`;
	}

	// Entry point for the MT5 quick-journal deep link: /journal/quick/:ticket?tickets=..&account=..&symbol=..&side=..&tf=..&grade=..&score=..
	public static async initQuick() {
		tradeJournal.init();
		if (!tradeJournal.modal) { return; }

		const params = new URLSearchParams(window.location.search);
		const primary = decodeURIComponent(window.location.pathname.split('/').pop() || '');
		const account = params.get('account') || '';
		const ticketsParam = params.get('tickets');

		tradeJournal.quickMode = true;
		tradeJournal.groupTickets = ticketsParam ? ticketsParam.split(',').filter(Boolean) : (primary ? [primary] : []);

		const status = d.qs<HTMLElement>('#quickJournalStatus');

		if (!primary || !account) {
			if (status) { status.textContent = 'Missing trade reference in the link.'; }
			return;
		}

		await tradeJournal.loadStrategies();
		await tradeJournal.openForTicket(primary, account);

		// Enrich the modal header with the known trade context.
		const grade = params.get('grade');
		const score = params.get('score');
		const ctxParts = [params.get('symbol'), params.get('side'), params.get('tf')];

		if (grade) { ctxParts.push(score ? `Grade ${grade} (${Math.round(parseFloat(score) * 100)}%)` : `Grade ${grade}`); }

		const ctx = ctxParts.filter(Boolean).join(' · ');
		const extra = tradeJournal.groupTickets.length > 1 ? ` · ${tradeJournal.groupTickets.length} entries` : '';
		const display = d.qs<HTMLElement>('#journalTicketDisplay');

		if (display) { display.textContent = `#${primary}${ctx ? ' · ' + ctx : ''}${extra}`; }
	}

	private static finishQuick() {
		tradeJournal.closeModal();

		const status = d.qs<HTMLElement>('#quickJournalStatus');
		if (status) { status.textContent = 'Saved ✓ - you can close this tab.'; }

		window.close(); // no-op unless the tab was script-opened, which is fine
	}
}
